import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useXHR from '../../hooks/useXHR';
import { API } from '../../utils/api';
import CompactResultRow from '../shared/CompactResultRow';

/**
 * FriendsProtocolBrowser - Browse and copy protocols shared by friends
 * Shows protocols from friends with "Friends Only" or "Public" privacy
 * Now includes auto-population of results when checking categories
 */
const FriendsProtocolBrowser = ({ showToast, onProtocolCopied, onResultsLoaded }) => {
  const { token } = useAuth();
  const { get, post, del: xhrDel, put } = useXHR(token);
  const [loading, setLoading] = useState(true);
  const [friendsData, setFriendsData] = useState([]);
  const [totalProtocols, setTotalProtocols] = useState(0);
  const [expandedFriends, setExpandedFriends] = useState({});
  const [copyingProtocol, setCopyingProtocol] = useState(null);
  const [customName, setCustomName] = useState('');
  const [showCopyModal, setShowCopyModal] = useState(null);
  
  // New: Selected categories and their results
  const [selectedProtocols, setSelectedProtocols] = useState({}); // { categoryId: { friend, protocol, results, loading } }
  const [combinedResults, setCombinedResults] = useState([]);

  // Fetch shared protocols from friends
  const fetchSharedProtocols = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const data = await get('/categories/shared-with-me');
      setFriendsData(data.friends || []);
      setTotalProtocols(data.total_protocols || 0);
      
      if (data.friends?.length > 0) {
        setExpandedFriends({ [data.friends[0].friend_id]: true });
      }
    } catch (e) {
      showToast(typeof e === 'string' ? e : 'Failed to load shared protocols', 'error');
    }
    setLoading(false);
  }, [get, token, showToast]);

  useEffect(() => {
    fetchSharedProtocols();
  }, [fetchSharedProtocols]);

  const toggleFriendExpand = (friendId) => {
    setExpandedFriends(prev => ({
      ...prev,
      [friendId]: !prev[friendId]
    }));
  };

  const fetchCategoryResults = useCallback(async (friendId, categoryId) => {
    try {
      const data = await get(`/users/${friendId}/categories/${categoryId}/results?limit=100`);
      return data.results || [];
    } catch (e) {
      console.error('Failed to fetch category results:', e);
    }
    return [];
  }, [get]);

  // NEW: Handle category checkbox toggle
  const handleCategoryToggle = async (friend, protocol) => {
    const categoryId = protocol.id;
    const isSelected = !!selectedProtocols[categoryId];
    
    if (isSelected) {
      // Deselect - remove from selected
      const newSelected = { ...selectedProtocols };
      delete newSelected[categoryId];
      setSelectedProtocols(newSelected);
    } else {
      // Select - fetch results
      setSelectedProtocols(prev => ({
        ...prev,
        [categoryId]: { friend, protocol, results: [], loading: true }
      }));
      
      const results = await fetchCategoryResults(friend.friend_id, categoryId);
      
      setSelectedProtocols(prev => ({
        ...prev,
        [categoryId]: { friend, protocol, results, loading: false }
      }));
      
      showToast(`Loaded ${results.length} results from "${protocol.name}"`, 'success');
    }
  };

  // Combine all selected results
  useEffect(() => {
    const allResults = [];
    Object.values(selectedProtocols).forEach(({ results, friend, protocol }) => {
      results.forEach(r => {
        allResults.push({
          ...r,
          fromFriend: friend.friend_name,
          fromProtocol: protocol.name
        });
      });
    });
    setCombinedResults(allResults);
    
    // Notify parent of results
    if (onResultsLoaded) {
      onResultsLoaded(allResults);
    }
  }, [selectedProtocols, onResultsLoaded]);

  const copyProtocol = async (protocol, friend) => {
    setCopyingProtocol(protocol.id);
    
    try {
      const data = await post('/categories/copy-from-friend', {
        body: { source_category_id: protocol.id, new_name: customName || undefined }
      });
      showToast(`Copied "${protocol.name}" from ${friend.friend_name}!`, 'success');
      setShowCopyModal(null);
      setCustomName('');
      onProtocolCopied && onProtocolCopied(data.category);
    } catch (e) {
      showToast(typeof e === 'string' ? e : 'Failed to copy protocol', 'error');
    }
    
    setCopyingProtocol(null);
  };

  const quickCopyProtocol = async (protocol, friend, includeContents) => {
    const copyKey = `${protocol.id}-${includeContents ? 'full' : 'cat'}`;
    setCopyingProtocol(copyKey);
    try {
      const data = await post('/categories/copy-protocol', {
        body: {
          source_category_id: protocol.id,
          include_contents: includeContents,
          new_name: protocol.name
        }
      });
      const mode = includeContents ? 'with contents' : 'category only';
      showToast(data.message || `Copied "${protocol.name}" (${mode})!`, 'success');
      onProtocolCopied && onProtocolCopied(data.category);
    } catch (e) {
      showToast('Copy failed', 'error');
    }
    setCopyingProtocol(null);
  };

  // Open copy modal
  const openCopyModal = (protocol, friend) => {
    setShowCopyModal({ protocol, friend });
    setCustomName(protocol.name);
  };

  // Check if category is selected
  const isProtocolSelected = (categoryId) => !!selectedProtocols[categoryId];

  // NEW: Select All / Deselect All functionality
  const selectAllProtocols = async () => {
    const allProtocols = [];
    friendsData.forEach(friend => {
      friend.protocols.forEach(protocol => {
        allProtocols.push({ friend, protocol });
      });
    });
    
    // Load all at once
    for (const { friend, protocol } of allProtocols) {
      if (!selectedProtocols[protocol.id]) {
        await handleCategoryToggle(friend, protocol);
      }
    }
  };

  const deselectAllProtocols = () => {
    setSelectedProtocols({});
    showToast('Cleared all selected protocols', 'info');
  };

  // Check if all protocols are selected
  const allProtocolsSelected = useMemo(() => {
    if (friendsData.length === 0) return false;
    let allSelected = true;
    friendsData.forEach(friend => {
      friend.protocols.forEach(protocol => {
        if (!selectedProtocols[protocol.id]) {
          allSelected = false;
        }
      });
    });
    return allSelected;
  }, [friendsData, selectedProtocols]);

  if (loading) {
    return (
      <div style={{
        padding: 30,
        textAlign: 'center',
        color: '#a1a1aa'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: 10 }}>👥</div>
        Loading shared protocols from friends...
      </div>
    );
  }

  if (friendsData.length === 0) {
    return (
      <div style={{
        padding: 30,
        textAlign: 'center',
        background: '#475569',
        borderRadius: 12,
        border: '1px dashed rgba(253, 224, 71, 0.3)'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: 15 }}>👥</div>
        <h3 style={{ color: '#047857', marginBottom: 10 }}>No Shared Protocols Found</h3>
        <p style={{ color: '#71717a', fontSize: '0.9rem', maxWidth: 400, margin: '0 auto' }}>
          Your friends haven't shared any protocols yet. When friends set their categories 
          to "Friends Only" or "Public", they'll appear here for you to copy!
        </p>
      </div>
    );
  }

  return (
    <div data-testid="friends-protocol-browser" style={{ display: 'flex', gap: 20 }}>
      {/* Left: Friends and Protocols (Categories only) */}
      <div style={{ width: 320, flexShrink: 0 }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10
        }}>
          <div>
            <h3 style={{ color: '#047857', margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem' }}>
              <span>👥</span> Friends' Protocols
            </h3>
            <p style={{ color: '#71717a', fontSize: '0.7rem', margin: '3px 0 0 0' }}>
              Check to load results • {totalProtocols} available
            </p>
          </div>
          <button
            onClick={fetchSharedProtocols}
            style={{
              background: 'rgba(253, 224, 71, 0.2)',
              border: '1px solid rgba(253, 224, 71, 0.4)',
              color: '#047857',
              padding: '5px 10px',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.75rem'
            }}
          >
            Refresh
          </button>
        </div>

        {/* Select All / Deselect All Controls */}
        <div style={{
          display: 'flex',
          gap: 6,
          marginBottom: 10,
          padding: 8,
          background: '#475569',
          borderRadius: 8
        }}>
          <button
            onClick={selectAllProtocols}
            disabled={allProtocolsSelected}
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 6,
              border: 'none',
              background: allProtocolsSelected ? 'rgba(71, 71, 71, 0.3)' : 'rgba(16, 185, 129, 0.2)',
              color: allProtocolsSelected ? '#71717a' : '#10b981',
              cursor: allProtocolsSelected ? 'default' : 'pointer',
              fontSize: '0.75rem',
              fontWeight: 600
            }}
            data-testid="select-all-protocols"
          >
            ✓ Select All
          </button>
          <button
            onClick={deselectAllProtocols}
            disabled={Object.keys(selectedProtocols).length === 0}
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 6,
              border: 'none',
              background: Object.keys(selectedProtocols).length === 0 ? 'rgba(71, 71, 71, 0.3)' : 'rgba(239, 68, 68, 0.2)',
              color: Object.keys(selectedProtocols).length === 0 ? '#71717a' : '#ef4444',
              cursor: Object.keys(selectedProtocols).length === 0 ? 'default' : 'pointer',
              fontSize: '0.75rem',
              fontWeight: 600
            }}
            data-testid="deselect-all-protocols"
          >
            ✕ Deselect All
          </button>
        </div>

        {/* Friends List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
          {friendsData.map(friend => (
            <div 
              key={friend.friend_id}
              style={{
                background: 'linear-gradient(135deg, #1e293b, rgba(20, 10, 40, 0.8))',
                borderRadius: 10,
                border: '1px solid rgba(253, 224, 71, 0.2)',
                overflow: 'hidden'
              }}
            >
              {/* Friend Header */}
              <div
                onClick={() => toggleFriendExpand(friend.friend_id)}
                style={{
                  padding: '10px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  background: expandedFriends[friend.friend_id] ? 'rgba(253, 224, 71, 0.15)' : 'transparent'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #FDE047, #FDE68A)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#0F172A',
                    fontWeight: 700,
                    fontSize: '0.9rem'
                  }}>
                    {friend.friend_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ color: '#334155', fontWeight: 600, fontSize: '0.85rem' }}>
                      {friend.friend_name}
                    </div>
                    <div style={{ color: '#71717a', fontSize: '0.7rem' }}>
                      {friend.protocol_count} protocols
                    </div>
                  </div>
                </div>
                <span style={{ 
                  color: '#047857', 
                  fontSize: '0.9rem',
                  transform: expandedFriends[friend.friend_id] ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}>
                  ▼
                </span>
              </div>

              {/* Protocols List with Checkboxes */}
              {expandedFriends[friend.friend_id] && (
                <div style={{ padding: '0 10px 10px 10px' }}>
                  {friend.protocols.map(protocol => {
                    const isSelected = isProtocolSelected(protocol.id);
                    const protocolData = selectedProtocols[protocol.id];
                    
                    return (
                      <div
                        key={protocol.id}
                        style={{
                          padding: '8px 10px',
                          background: isSelected ? 'rgba(16, 185, 129, 0.15)' : 'rgba(0, 0, 0, 0.2)',
                          borderRadius: 8,
                          marginTop: 6,
                          border: isSelected ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid transparent'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleCategoryToggle(friend, protocol)}
                            disabled={protocolData?.loading}
                            style={{
                              width: 16,
                              height: 16,
                              accentColor: '#10b981',
                              cursor: 'pointer'
                            }}
                            data-testid={`protocol-checkbox-${protocol.id}`}
                          />
                          
                          {/* Protocol Name (clickable) */}
                          <div 
                            onClick={() => handleCategoryToggle(friend, protocol)}
                            style={{ 
                              flex: 1, 
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6
                            }}
                          >
                            <span style={{ 
                              color: isSelected ? '#10b981' : '#e2e8f0', 
                              fontWeight: 500,
                              fontSize: '0.85rem'
                            }}>
                              {protocol.name}
                            </span>
                            {protocolData?.loading && (
                              <span style={{ fontSize: '0.7rem', color: '#f59e0b' }}>Loading...</span>
                            )}
                            {protocolData && !protocolData.loading && (
                              <span style={{ fontSize: '0.7rem', color: '#10b981' }}>
                                ({protocolData.results.length})
                              </span>
                            )}
                          </div>
                          
                          {/* Privacy Badge */}
                          <span style={{
                            fontSize: '0.6rem',
                            padding: '2px 6px',
                            borderRadius: 8,
                            background: protocol.privacy === 'public' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                            color: protocol.privacy === 'public' ? '#10b981' : '#f59e0b'
                          }}>
                            {protocol.privacy === 'public' ? '🌍' : '👥'}
                          </span>
                          
                          {/* Copy Category Button */}
                          <button
                            onClick={(e) => { e.stopPropagation(); quickCopyProtocol(protocol, friend, false); }}
                            disabled={copyingProtocol === `${protocol.id}-cat`}
                            data-testid={`friend-copy-category-${protocol.id}`}
                            style={{
                              background: 'rgba(245, 158, 11, 0.15)',
                              border: '1px solid rgba(245, 158, 11, 0.3)',
                              color: '#047857',
                              padding: '3px 6px',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: '0.55rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}
                            title="Copy just the category name and rules"
                          >
                            {copyingProtocol === `${protocol.id}-cat` ? '...' : 'Copy Cat.'}
                          </button>
                          
                          {/* Copy Category & Contents Button */}
                          <button
                            onClick={(e) => { e.stopPropagation(); quickCopyProtocol(protocol, friend, true); }}
                            disabled={copyingProtocol === `${protocol.id}-full`}
                            data-testid={`friend-copy-contents-${protocol.id}`}
                            style={{
                              background: 'rgba(16, 185, 129, 0.15)',
                              border: '1px solid rgba(16, 185, 129, 0.3)',
                              color: '#34d399',
                              padding: '3px 6px',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: '0.55rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}
                            title="Copy the category rules AND all associated URLs"
                          >
                            {copyingProtocol === `${protocol.id}-full` ? '...' : 'Copy + URLs'}
                          </button>
                          
                          {/* Full Copy Button (opens modal) */}
                          <button
                            onClick={(e) => { e.stopPropagation(); openCopyModal(protocol, friend); }}
                            style={{
                              background: 'rgba(253, 224, 71, 0.2)',
                              border: 'none',
                              color: '#047857',
                              padding: '4px 8px',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: '0.7rem'
                            }}
                            title="Copy with custom name"
                          >
                            ...
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Right: Combined Results Display */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.4)',
          borderRadius: 12,
          padding: 15,
          border: '1px solid rgba(253, 224, 71, 0.2)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 12
          }}>
            <h4 style={{ color: '#047857', margin: 0, fontSize: '0.9rem' }}>
              📄 Results from Selected Protocols ({combinedResults.length})
            </h4>
            {Object.keys(selectedProtocols).length > 0 && (
              <button
                onClick={() => setSelectedProtocols({})}
                style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: 'none',
                  color: '#ef4444',
                  padding: '4px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.75rem'
                }}
              >
                Clear All
              </button>
            )}
          </div>

          {combinedResults.length === 0 ? (
            <div style={{
              padding: 40,
              textAlign: 'center',
              color: '#71717a'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: 10 }}>📂</div>
              <p style={{ margin: 0 }}>Check categories on the left to load their results</p>
              <p style={{ fontSize: '0.8rem', marginTop: 5 }}>Results will appear here instantly</p>
            </div>
          ) : (
            <div style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
              {combinedResults.map((result, idx) => (
                <CompactResultRow
                  key={result.id || idx}
                  result={result}
                  token={token}
                  showToast={showToast}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Copy Modal */}
      {showCopyModal && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowCopyModal(null)}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, #FFFFFF, #2d1f4e)',
              borderRadius: 16,
              padding: 25,
              maxWidth: 450,
              width: '90%',
              border: '2px solid rgba(16, 185, 129, 0.4)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ color: '#10b981', marginTop: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span>📋</span> Copy Protocol
            </h3>
            
            <p style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>
              Copy <strong style={{ color: '#334155' }}>"{showCopyModal.protocol.name}"</strong> from{' '}
              <strong style={{ color: '#047857' }}>{showCopyModal.friend.friend_name}</strong> to your database.
            </p>
            
            <div style={{ marginTop: 20 }}>
              <label style={{ color: '#047857', fontSize: '0.85rem', display: 'block', marginBottom: 8 }}>
                Category Name:
              </label>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder={showCopyModal.protocol.name}
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  borderRadius: 8,
                  border: '1px solid rgba(253, 224, 71, 0.3)',
                  background: '#475569',
                  color: '#0F172A',
                  fontSize: '0.95rem'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button
                onClick={() => setShowCopyModal(null)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: 8,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'transparent',
                  color: '#a1a1aa',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => copyProtocol(showCopyModal.protocol, showCopyModal.friend)}
                disabled={copyingProtocol}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  color: '#0F172A',
                  cursor: copyingProtocol ? 'wait' : 'pointer',
                  fontWeight: 600
                }}
              >
                {copyingProtocol ? 'Copying...' : 'Copy to My Database'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendsProtocolBrowser;
